/**
 * Pre-buy "Want the full setup?" — gating, offer-kind filter, and the
 * visibility-paused countdown. Extracted from the morph so the rules can be
 * unit-tested without rendering the pill.
 *
 * Frequency:
 *   Amazon     → once per browser session (global key). Conservative: we
 *                never nag after an affiliate click.
 *   non-Amazon → once per product per session. Non-selling clicks carry no
 *                affiliate risk, so the same visitor can still be asked on
 *                the next product.
 *
 * An Amazon prompt also writes the per-product key, so the same product is
 * not asked twice in one session if the visitor then clicks a non-Amazon
 * row. The reverse is also true: a non-Amazon prompt on this product blocks
 * a later Amazon prompt on it, without blocking Amazon on other products.
 */

export const AMAZON_SESSION_KEY = 'iai-prebuy-prompted';
export const PRODUCT_SESSION_PREFIX = 'iai-prebuy-prompted:';

/** Confirm chip ("Opened on {merchant}") before the ask. */
export const CONFIRM_MS = 900;
/** Auto-revert budget, counted only while the document is visible. */
export const ASK_VISIBLE_MS = 45_000;

export type PrebuyFire = {
  merchantName: string;
  isAmazon: boolean;
  merchantDomain?: string;
  /** Bumps to trigger the morph; the parent owns this. */
  nonce: number;
};

export type PrebuyGate = {
  isAmazon: boolean;
  categoryId?: string;
  asin: string;
};

export type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

export const productSessionKey = (categoryId: string | undefined, asin: string): string =>
  `${PRODUCT_SESSION_PREFIX}${categoryId ?? ''}:${asin}`;

const liveStorage = (): StorageLike | null => {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
};

/**
 * True when this click should not morph. Storage unavailable → never prompt
 * (same fail-closed as the original sessionStorage gate).
 */
export const alreadyPrompted = (gate: PrebuyGate, storage?: StorageLike): boolean => {
  const store = storage ?? liveStorage();
  if (!store) return true;
  try {
    if (store.getItem(productSessionKey(gate.categoryId, gate.asin)) === '1') return true;
    if (gate.isAmazon && store.getItem(AMAZON_SESSION_KEY) === '1') return true;
    return false;
  } catch {
    return true;
  }
};

export const markPrompted = (gate: PrebuyGate, storage?: StorageLike): void => {
  const store = storage ?? liveStorage();
  if (!store) return;
  try {
    store.setItem(productSessionKey(gate.categoryId, gate.asin), '1');
    if (gate.isAmazon) store.setItem(AMAZON_SESSION_KEY, '1');
  } catch {
    /* ignore */
  }
};

/**
 * Amazon-search rows are not a purchase of THIS product — seeding a kit with
 * "I'm buying the {title}" would be a false claim. Default `kind` is 'offer'.
 */
export const shouldFirePrebuyForOffer = (offer: { kind?: 'offer' | 'search' }): boolean =>
  offer.kind !== 'search';

/**
 * Remaining auto-revert (or confirm) budget after subtracting time spent
 * with the document hidden. `hiddenStartedAt` is when the tab last hid, or
 * null while visible. `hiddenAccumulatedMs` is completed hidden intervals.
 */
export const remainingVisibleMs = ({
  budgetMs,
  startedAt,
  now,
  hiddenStartedAt,
  hiddenAccumulatedMs,
}: {
  budgetMs: number;
  startedAt: number;
  now: number;
  hiddenStartedAt: number | null;
  hiddenAccumulatedMs: number;
}): number => {
  const hiddenNow =
    hiddenStartedAt !== null ? Math.max(0, now - hiddenStartedAt) : 0;
  const elapsedVisible = Math.max(
    0,
    now - startedAt - hiddenAccumulatedMs - hiddenNow,
  );
  return Math.max(0, budgetMs - elapsedVisible);
};
